import { Transforms } from 'slate';

import { Editor, Element } from '@@editor/helpers';
import { MIME_TYPES } from '@@editor/helpers/Element';
import renderEditor from '@@editor/plugins/utils/renderEditor';
import renderElement from '@@editor/plugins/utils/renderElement';
import { type GenerateEmbedBlockData } from '@@editor/typings/Embed';
import { PLUGIN_NAMES } from '@@editor/typings/UnityPlugins';

import { NODE_TYPE, TYPE } from './constants';
import { createGenerateEmbedBlock } from '../utils';
import EmbeddedSnippetNode from './components/EmbeddedSnippetNode';
import EditorWithEmbedModal from '../components/EditorWithEmbedModal';
import { generateEmbedBlock as generateEmbeddedSnippet } from '../embeddedSnippet/plugin';
import EmbeddedSnippetForm from './components/EmbeddedSnippetForm';
import { removePollIds } from '../../poll/utils';

const generateSnippetContent = (editor, formData, options) => {
    Editor.insertElement(
        editor,
        formData.element[0].data.content.map((node) => {
            // In case we extract an editable copy a poll from an existing snippet,
            // we do not want the ids to be copied along, as it would affect the results
            // of all the polls that were created from that snippet
            if (Element.isPollElement(node)) {
                return Element.create(removePollIds(node));
            }

            return Element.create(node);
        }),
        options,
    );
};

export const generateEmbedBlockData: GenerateEmbedBlockData = ({ embed }) => ({
    mimetype: MIME_TYPES.EMBEDDED_SNIPPET,
    embed,
});

export const generateEmbedBlock = createGenerateEmbedBlock({
    type: TYPE,
    nodeType: NODE_TYPE,
    generateEmbedBlockData,
});

const internalWithEmbeddedSnippet = (editor, options) =>
    Object.assign(editor, {
        renderEditor: renderEditor(
            editor,
            (props) => (
                <EditorWithEmbedModal
                    {...props}
                    formProps={{ editor, formName: 'EmbeddedSnippetForm' }}
                    formComponent={EmbeddedSnippetForm}
                    generateEmbedBlock={() => {}}
                    type={PLUGIN_NAMES.EMBEDDED_SNIPPET}
                />
            ),
            options,
        ),
        renderElement: renderElement(editor, [[NODE_TYPE, EmbeddedSnippetNode]], options),
        insertEmbeddedSnippet: (formData, type, options) => {
            generateEmbeddedSnippet(editor, formData.embed, options);
        },
        insertLayoutComponent: (formData, type, options) => {
            if (options.replace) {
                Transforms.removeNodes(editor, { at: options.path });
                generateSnippetContent(editor, formData, { at: options.at });
                Transforms.select(editor, options.at);
                Transforms.collapse(editor);
            } else {
                generateSnippetContent(editor, formData, { at: options.at });
            }
        },
    });

export const withEmbeddedSnippet = (editor, options) =>
    internalWithEmbeddedSnippet(editor, {
        ...options,
        nodeType: [NODE_TYPE],
    });

export default withEmbeddedSnippet;
